import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, InputSignal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TransportMean, TransportMeanIconName } from '../../../interfaces/register-user.response';
import { normalizeValue } from '../../../utils/value-normalizer';
import { RegisterUserFormService } from '../../../services/register-user-form.service';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseFormField } from '../../form/abstracts';
import { rawTransportMeans } from './config/constants';
import { MatError } from '@angular/material/form-field';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';

@Component({
  selector: 'app-transport-means-field',
  standalone: true,
  imports: [
    TranslateModule,
    MatError
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: TransportMeansFieldComponent
    },
  ],
  templateUrl: './transport-means-field.component.html',
  styleUrls: ['./transport-means-field.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportMeansFieldComponent extends BaseFormField<TransportMean[]> {
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlName: InputSignal<string> = input.required<string>();
  public isLastSlideValidationActive: InputSignal<boolean> = input<boolean>();
  
  private _cdr = inject(ChangeDetectorRef);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _breakpointsService = inject(BreakpointsService);
  
  private _formControlName =  () => this.form().get(this.formControlName());
  private _currentTransportMeans = this._registerUserFormService.transportMeans;
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  private _isMobile = this._breakpointsService.isMobile;
  
  public transportMeans: TransportMean[] = 
    rawTransportMeans.map((transport, index) => {
        return {
          id: index,
          label: transport.label,
        value: normalizeValue(transport.label),
        iconName: transport.iconName.trim() as TransportMeanIconName,
        eco: transport.eco,
      }
    })
    
  markAllFieldsAsTouchedForDesktop = effect(() => {
    if (this._markAllFormFieldsAsTouched()) {
      const control = this._formControlName();
      
      if (!control) return;
      
      // En mobile: NO marcar automáticamente como touched para evitar errores iniciales
      // Solo se marcará como touched cuando el usuario presione "Continuar" sin seleccionar
      if (this._isMobile()) {
        return;
      }
      
      control?.markAsTouched();
      control?.markAsDirty();

      // Triggear change detection manually for OnPush
      this._cdr.detectChanges();
    }
  }, { allowSignalWrites: true });
  
  public handleChipSetTransportMeansChange(event: Event) {
    const customEvent = event as CustomEvent<{ values: string[] }>;
    const selectedTransportMeans = customEvent.detail.values;

    this._registerUserFormService.setTransportMeans(selectedTransportMeans);
    this.form().get(this.formControlName())?.setValue(selectedTransportMeans);
    
    const control = this.form().get(this.formControlName());
    control?.markAsTouched();
    control?.markAsDirty();
    
    if (this._isMobile()) {
      this._registerUserFormService.setMarkAllFormFieldsAsTouched(false);
      closeAllCiSnackbar();
    }
  }
  
  public isInvalidField(): boolean {
    const control = this.form().get(this.formControlName());
    if (!control) return false;
    
    if (this._isMobile()) {
      // mobile: check only when last slide validation is active
      if (this.isLastSlideValidationActive()) {
        return control?.hasError('required') && (control?.touched || control?.dirty);
      } else {
        return false;
      }
    } else {
      // desktop: normal behavior
      return control?.hasError('required') && (control?.touched || control?.dirty);
    }
  }
  
  public isSelectedTransportMean(value: string): boolean {
    return this._currentTransportMeans().includes(value);
  }
}
